import { __extends, __assign } from 'tslib';
import { useLocalization } from '@shared-ui/localization-context';
import { Viewport, ViewSmall, ViewLarge } from '@shared-ui/viewport-context';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { UitkFullscreenDialog, UitkDialogAnimationType } from 'uitk-react-dialog';
import { UitkTextInput, UitkFauxInput } from 'uitk-react-input';
import { UitkLayoutFlexItem, UitkLayoutFlex } from 'uitk-react-layout-flex';
import { classNames } from 'uitk-react-utils';
import { UitkMenu, UitkMenuTrigger, UitkMenuContainer, PositionType } from 'uitk-react-menu';
import styles from './uitk-date-picker.css';
import { UitkDatePickerRenderType } from './uitk-date-picker.esm.js';
import { UitkDatePickerDialogView } from './uitk-date-picker-dialog-view.esm.js';
import { UitkDatePickerMenuView } from './uitk-date-picker-menu-view.esm.js';

var getClassNameList = classNames.bind(styles);
var UitkDatePickerDateFormat;
(function (UitkDatePickerDateFormat) {
    UitkDatePickerDateFormat["YMD"] = "yyyy-MM-dd";
})(UitkDatePickerDateFormat || (UitkDatePickerDateFormat = {}));
var mapDatePickerProps = function (source) { return (__assign(__assign({}, source.datePickerProps), { onChangeEndDate: source.onChangeEndDate, onChangeMonth: source.onChangeMonth, onChangeStartDate: source.onChangeStartDate, onClearDates: source.onClearDates, onDismiss: source.onDismiss, onScrollHandler: source.onScrollHandler, onSubmit: source.onSubmit, preventAutoFocus: source.preventAutoFocus, singleMode: source.singleMode && !source.useSingleTrigger, useClearDates: source.useClearDates, useSingleTrigger: source.useSingleTrigger })); };
/**
 * Get display values and values for date field
 */
var getFormattedDates = function (l10n, dateProps, dateFormat, mediumDateFormatProp) {
    var mediumDateFormat = { dateFormat: 'medium', skeleton: mediumDateFormatProp !== null && mediumDateFormatProp !== void 0 ? mediumDateFormatProp : 'MMMd' };
    var longDateFormat = { dateFormat: 'long', skeleton: 'yMMMMd' };
    var isoDateFormat = { dateFormat: 'iso-date', raw: dateFormat };
    var value = dateProps.value, placeholder = dateProps.placeholder;
    return {
        value: l10n.formatDate(value, isoDateFormat),
        formattedBtnText: value ? l10n.formatDate(value, mediumDateFormat) : placeholder,
        accessibleFormattedText: value ? l10n.formatDate(value, longDateFormat) : placeholder,
        label: React.createElement("span", { "aria-hidden": "true" }, dateProps.label)
    };
};
var CustomTriggerDialog = function (props) {
    useEffect(function () {
        if (props.updateViewport) {
            props.updateViewport(true);
        }
    }, []);
    var enhancedDatePickerProps = mapDatePickerProps(props);
    return (React.createElement(UitkFullscreenDialog, { dialogShow: true, animation: UitkDialogAnimationType.SLIDEUP },
        React.createElement(UitkDatePickerDialogView, __assign({}, enhancedDatePickerProps))));
};
var CustomTriggerMenu = function (props) {
    useEffect(function () {
        if (props.updateViewport) {
            props.updateViewport(false);
        }
    }, []);
    var enhancedDatePickerProps = mapDatePickerProps(props);
    return React.createElement(UitkDatePickerMenuView, __assign({}, enhancedDatePickerProps));
};
CustomTriggerDialog.displayName = 'CustomTriggerDialog';
var DialogEndDateField = function (props) {
    var dateFormat = props.dateFormat, endProps = props.endProps, l10n = props.l10n, mediumDateFormatProp = props.mediumDateFormatProp, onChange = props.onChange, routerLinkWrapper = props.routerLinkWrapper;
    var _a = useState(false), isMounted = _a[0], setIsMounted = _a[1];
    useEffect(function () {
        setIsMounted(true);
    }, []);
    if (!endProps) {
        return null;
    }
    var _b = getFormattedDates(l10n, endProps, dateFormat, mediumDateFormatProp), value = _b.value, accessibleFormattedText = _b.accessibleFormattedText, formattedBtnText = _b.formattedBtnText, label = _b.label;
    var buttonLabel = "".concat(endProps.label, " ").concat(accessibleFormattedText);
    var setContext = function () {
        props.openDatePicker(endProps.dateType);
    };
    var textInputClasses = getClassNameList({
        'is-hidden': isMounted
    });
    return (React.createElement(UitkTextInput, { id: endProps.id, name: endProps.name, className: textInputClasses, label: label, placeholder: endProps.placeholder, value: value, onChange: onChange, "data-stid": "input-date", icon: "today", invalid: endProps.invalid }, isMounted &&
        routerLinkWrapper &&
        routerLinkWrapper(React.createElement(UitkFauxInput, { "aria-label": buttonLabel, className: getClassNameList('uitk-form-field-trigger'), domRef: endProps.domRef, "data-name": endProps.name, id: "".concat(endProps.id, "-btn"), "data-stid": "open-date-picker", type: "button", onClick: setContext, analytics: endProps.analytics }, formattedBtnText))));
};
DialogEndDateField.displayName = 'DialogEndDateField';
var DialogDateFields = function (props) {
    var _a;
    var _b = useState(false), isMounted = _b[0], setIsMounted = _b[1];
    useEffect(function () {
        setIsMounted(true);
        if (props.updateViewport) {
            props.updateViewport(true);
        }
    }, []);
    var dateFieldWrapperClasses = props.dateFieldWrapperClasses, dateFormat = props.dateFormat, dialogWrapper = props.dialogWrapper, l10n = props.l10n, mediumDateFormatProp = props.mediumDateFormatProp, onChange = props.onChange, routerLinkWrapper = props.routerLinkWrapper, showDatePicker = props.showDatePicker, singleMode = props.singleMode, space = props.space, stacked = props.stacked, startProps = props.startProps, endProps = props.endProps, triggeredField = props.triggeredField, useSingleTrigger = props.useSingleTrigger;
    var _c = getFormattedDates(l10n, startProps, dateFormat, mediumDateFormatProp), startValue = _c.value, startFormattedBtnText = _c.formattedBtnText, startAccessibleFormattedText = _c.accessibleFormattedText, startLabel = _c.label;
    var value = startValue;
    var formattedBtnText = startFormattedBtnText;
    var buttonLabel = "".concat(startProps.label);
    if (startAccessibleFormattedText) {
        buttonLabel += " ".concat(startAccessibleFormattedText);
    }
    if (useSingleTrigger && endProps) {
        var _d = getFormattedDates(l10n, endProps, dateFormat, mediumDateFormatProp), endValue = _d.value, endFormattedBtnText = _d.formattedBtnText, endAccessibleFormattedText = _d.accessibleFormattedText;
        if (endValue) {
            value += " - ".concat(endValue);
        }
        if (endFormattedBtnText) {
            formattedBtnText += " - ".concat(endFormattedBtnText);
        }
        if (endAccessibleFormattedText) {
            buttonLabel = "".concat(startProps.label, " ").concat(l10n.formatText('datepicker.buttonLabel.endAccessibleFormattedText', startAccessibleFormattedText, endAccessibleFormattedText));
        }
    }
    var setContext = function () {
        props.openDatePicker(startProps.dateType);
    };
    var shouldBeStacked = stacked && !singleMode && !useSingleTrigger;
    var fieldWrapperClasses = getClassNameList({
        'uitk-date-field-wrapper': !shouldBeStacked
    });
    var fieldLayoutDirection = shouldBeStacked ? 'column' : 'row';
    var wrapperClasses = getClassNameList((_a = {},
        _a["".concat(dateFieldWrapperClasses)] = dateFieldWrapperClasses,
        _a), 'uitk-date-fields-wrapper');
    var enhancedDatePickerProps = mapDatePickerProps(props);
    var textInputClasses = getClassNameList({
        'is-hidden': isMounted
    });
    return (React.createElement(UitkLayoutFlexItem, { className: wrapperClasses, grow: 1 },
        React.createElement(UitkLayoutFlex, { className: getClassNameList('uitk-date-fields'), direction: fieldLayoutDirection, space: space },
            showDatePicker &&
                triggeredField &&
                dialogWrapper(React.createElement(UitkFullscreenDialog, { dialogShow: true, animation: UitkDialogAnimationType.SLIDEUP },
                    React.createElement(UitkDatePickerDialogView, __assign({}, enhancedDatePickerProps))), triggeredField),
            React.createElement(UitkLayoutFlexItem, { className: fieldWrapperClasses, grow: 1, minWidth: "zero", maxWidth: singleMode || useSingleTrigger || stacked ? undefined : 'half_width' },
                React.createElement("div", null,
                    React.createElement(UitkTextInput, { id: startProps.id, name: startProps.name, className: textInputClasses, label: startLabel, placeholder: startProps.placeholder, value: value, onChange: onChange, "data-stid": "input-date", icon: "today", invalid: startProps.invalid }, isMounted &&
                        routerLinkWrapper &&
                        routerLinkWrapper(React.createElement(UitkFauxInput, { "aria-label": buttonLabel, className: getClassNameList('uitk-form-field-trigger'), domRef: startProps.domRef, "data-name": startProps.name, id: "".concat(startProps.id, "-btn"), "data-stid": "open-date-picker", type: "button", onClick: setContext, analytics: startProps.analytics }, formattedBtnText))))),
            !singleMode && !useSingleTrigger && (React.createElement(UitkLayoutFlexItem, { className: fieldWrapperClasses, grow: 1, minWidth: "zero", maxWidth: stacked ? undefined : 'half_width' },
                React.createElement("div", null,
                    React.createElement(DialogEndDateField, __assign({}, props))))))));
};
DialogDateFields.displayName = 'DialogDateFields';
var MenuEndDateField = function (props) {
    var dateFormat = props.dateFormat, mediumDateFormatProp = props.mediumDateFormatProp, endProps = props.endProps, l10n = props.l10n, onChange = props.onChange, onSubmit = props.onSubmit, showDatePicker = props.showDatePicker, triggeredField = props.triggeredField;
    var _a = useState(false), isMounted = _a[0], setIsMounted = _a[1];
    useEffect(function () {
        setIsMounted(true);
    }, []);
    if (!endProps) {
        return null;
    }
    var _b = getFormattedDates(l10n, endProps, dateFormat, mediumDateFormatProp), value = _b.value, formattedBtnText = _b.formattedBtnText, accessibleFormattedText = _b.accessibleFormattedText, label = _b.label;
    var buttonLabel = "".concat(endProps.label, " ").concat(accessibleFormattedText);
    var setContext = function () {
        props.openDatePicker(endProps.dateType);
    };
    // Make sure that both menus aren't opened at the same time on auto-open
    var isOpen = isMounted && showDatePicker;
    var containerClasses = getClassNameList('uitk-date-picker-menu-container', {
        'uitk-date-picker-menu-container-single': !endProps.doubleMonths,
        'uitk-date-picker-menu-container-double': endProps.doubleMonths
    });
    var enhancedDatePickerProps = mapDatePickerProps(props);
    var textInputClasses = getClassNameList({
        'is-hidden': isMounted
    });
    return (React.createElement(UitkMenu, { isOpen: isOpen, className: getClassNameList('uitk-date-picker-menu'), onDismiss: onSubmit },
        React.createElement(UitkMenuTrigger, null,
            React.createElement(UitkTextInput, { className: textInputClasses, id: endProps.id, name: endProps.name, label: label, placeholder: endProps.placeholder, value: value, onChange: onChange, "data-stid": "input-date", icon: "today", invalid: endProps.invalid }, isMounted && (React.createElement(UitkFauxInput, { "aria-label": buttonLabel, className: getClassNameList('uitk-form-field-trigger'), domRef: endProps.domRef, "data-name": endProps.name, id: "".concat(endProps.id, "-btn"), "data-stid": "open-date-picker", type: "button", onClick: setContext, analytics: endProps.analytics }, formattedBtnText)))),
        showDatePicker && triggeredField === 'end' && (React.createElement(UitkMenuContainer, { position: PositionType.LEFT, className: containerClasses, autoPosition: true },
            React.createElement(UitkDatePickerMenuView, __assign({}, enhancedDatePickerProps))))));
};
MenuEndDateField.displayName = 'MenuEndDateField';
var MenuDateFields = function (props) {
    var _a;
    var _b = useState(false), isMounted = _b[0], setIsMounted = _b[1];
    useEffect(function () {
        setIsMounted(true);
        if (props.updateViewport) {
            props.updateViewport(false);
        }
    }, []);
    var dateFieldWrapperClasses = props.dateFieldWrapperClasses, dateFormat = props.dateFormat, l10n = props.l10n, mediumDateFormatProp = props.mediumDateFormatProp, onChange = props.onChange, onSubmit = props.onSubmit, showDatePicker = props.showDatePicker, singleMode = props.singleMode, space = props.space, stacked = props.stacked, startProps = props.startProps, endProps = props.endProps, triggeredField = props.triggeredField, useSingleTrigger = props.useSingleTrigger;
    var _c = getFormattedDates(l10n, startProps, dateFormat, mediumDateFormatProp), startValue = _c.value, startFormattedBtnText = _c.formattedBtnText, startAccessibleFormattedText = _c.accessibleFormattedText, startLabel = _c.label;
    var value = startValue;
    var formattedBtnText = startFormattedBtnText;
    var buttonLabel = "".concat(startProps.label);
    if (startAccessibleFormattedText) {
        buttonLabel += " ".concat(startAccessibleFormattedText);
    }
    if (useSingleTrigger && endProps) {
        var _d = getFormattedDates(l10n, endProps, dateFormat, mediumDateFormatProp), endValue = _d.value, endFormattedBtnText = _d.formattedBtnText, endAccessibleFormattedText = _d.accessibleFormattedText;
        if (endValue) {
            value += " - ".concat(endValue);
        }
        if (endFormattedBtnText) {
            formattedBtnText += " - ".concat(endFormattedBtnText);
        }
        if (endAccessibleFormattedText) {
            buttonLabel = "".concat(startProps.label, " ").concat(l10n.formatText('datepicker.buttonLabel.endAccessibleFormattedText', startAccessibleFormattedText, endAccessibleFormattedText));
        }
    }
    var setContext = function () {
        props.openDatePicker(startProps.dateType);
    };
    var isOpen = showDatePicker;
    var containerClasses = getClassNameList('uitk-date-picker-menu-container', {
        'uitk-date-picker-menu-container-single': !startProps.doubleMonths,
        'uitk-date-picker-menu-container-double': startProps.doubleMonths
    });
    var enhancedDatePickerProps = mapDatePickerProps(props);
    var shouldBeStacked = stacked && !singleMode && !useSingleTrigger;
    var fieldWrapperClasses = getClassNameList({
        'uitk-date-field-wrapper': !shouldBeStacked
    });
    var fieldLayoutDirection = shouldBeStacked ? 'column' : 'row';
    var wrapperClasses = getClassNameList((_a = {},
        _a["".concat(dateFieldWrapperClasses)] = dateFieldWrapperClasses,
        _a), 'uitk-date-fields-wrapper');
    var textInputClasses = getClassNameList({
        'is-hidden': isMounted
    });
    return (React.createElement(UitkLayoutFlexItem, { className: wrapperClasses, grow: 1 },
        React.createElement(UitkLayoutFlex, { className: getClassNameList('uitk-date-fields'), space: space, direction: fieldLayoutDirection },
            React.createElement(UitkLayoutFlexItem, { className: fieldWrapperClasses, grow: 1, minWidth: "zero", maxWidth: singleMode || useSingleTrigger || stacked ? undefined : 'half_width' },
                React.createElement("div", null,
                    React.createElement(UitkMenu, { isOpen: isOpen, className: getClassNameList('uitk-date-picker-menu'), onDismiss: onSubmit },
                        React.createElement(UitkMenuTrigger, null,
                            React.createElement(UitkTextInput, { className: textInputClasses, id: startProps.id, name: startProps.name, label: startLabel, placeholder: startProps.placeholder, value: value, onChange: onChange, "data-stid": "input-date", icon: "today", invalid: startProps.invalid }, isMounted && (React.createElement(UitkFauxInput, { "aria-label": buttonLabel, className: getClassNameList('uitk-form-field-trigger'), domRef: startProps.domRef, "data-name": startProps.name, id: "".concat(startProps.id, "-btn"), "data-stid": "open-date-picker", type: "button", onClick: setContext, analytics: startProps.analytics }, formattedBtnText)))),
                        showDatePicker && triggeredField === 'start' && (React.createElement(UitkMenuContainer, { position: PositionType.LEFT, className: containerClasses, autoPosition: true },
                            React.createElement(UitkDatePickerMenuView, __assign({}, enhancedDatePickerProps))))))),
            !singleMode && !useSingleTrigger && (React.createElement(UitkLayoutFlexItem, { className: fieldWrapperClasses, grow: 1, minWidth: "zero", maxWidth: stacked ? undefined : 'half_width' },
                React.createElement("div", null,
                    React.createElement(MenuEndDateField, __assign({}, props))))))));
};
MenuDateFields.displayName = 'MenuDateFields';
var DateField = /** @class */ (function (_super) {
    __extends(DateField, _super);
    function DateField() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    DateField.prototype.render = function () {
        var _a = this.props, useCustomTrigger = _a.useCustomTrigger, datePickerProps = _a.datePickerProps;
        if (useCustomTrigger) {
            switch (datePickerProps.renderType) {
                case UitkDatePickerRenderType.DIALOG:
                    return React.createElement(CustomTriggerDialog, __assign({}, this.props));
                case UitkDatePickerRenderType.MENU:
                    return React.createElement(CustomTriggerMenu, __assign({}, this.props));
                default:
                    return (React.createElement(Viewport, null,
                        React.createElement(ViewSmall, null,
                            React.createElement(CustomTriggerDialog, __assign({}, this.props))),
                        React.createElement(ViewLarge, null,
                            React.createElement(CustomTriggerMenu, __assign({}, this.props)))));
            }
        }
        switch (datePickerProps.renderType) {
            case UitkDatePickerRenderType.DIALOG:
                return React.createElement(DialogDateFields, __assign({}, this.props));
            case UitkDatePickerRenderType.MENU:
                return React.createElement(MenuDateFields, __assign({}, this.props));
            default:
                return (React.createElement(Viewport, null,
                    React.createElement(ViewSmall, null,
                        React.createElement(DialogDateFields, __assign({}, this.props))),
                    React.createElement(ViewLarge, null,
                        React.createElement(MenuDateFields, __assign({}, this.props)))));
        }
    };
    DateField.displayName = 'UitkDateField';
    DateField.defaultProps = {
        dateFormat: UitkDatePickerDateFormat.YMD,
        space: 'three',
        triggeredField: 'start'
    };
    return DateField;
}(React.Component));
var UitkDateField = function (props) {
    var l10n = useLocalization();
    return React.createElement(DateField, __assign({ l10n: l10n }, props));
};
UitkDateField.displayName = 'UitkDateField';
var UitkNewDateField = UitkDateField;

export { UitkDateField, UitkDatePickerDateFormat, UitkNewDateField };
